// @flow
import { endsWith } from "voca";
import appConfig from "appConfig";
import { consts as uploaderConsts } from "uploader";
import { getLogger } from "common/logger";
import { openWindow } from "utils/domUtils";
import { api, common as servicesCommon, createTtlHandler } from "services";
import instConsts from "../data/consts";

const FIELDS = "media_type,media_url,thumbnail_url";

export const MEDIA_FIELDS = `caption,${FIELDS},children{${FIELDS}}`;

const logger = getLogger();
const { authServer } = appConfig.instagram;
const fetchCount = appConfig.instagram.maxResults;
const SRCS = uploaderConsts.SOURCES;

let authWin = null;

type getParams = { cloudName: string, extend?: ?boolean, url: ?string, items?: ?Object[] }
type authInfoParams = { token: ?string, cloudName: string };
type Config = { cloudName: string, server: ?string, newTlsDomain? : boolean };

const tokenTtlHandler = createTtlHandler(instConsts.TOKEN_TTL_MINUTES);

const getAuthenticationUrl = () => `${authServer}instagram/`;

const getAuthInfo = (cloudName: string): { token: ?string, cloudName: string } => ({
	cloudName,
	token: servicesCommon.getStoredAuthToken(cloudName, SRCS.INSTAGRAM),
});

const setAuthListener = (cloudName: string) => servicesCommon.setMessageAuthListener(() => authWin, cloudName, SRCS.INSTAGRAM)
	.then((result) => {
		authWin = null;
		return result;
	});

const logout = (cloudName: string) => {
	servicesCommon.removeAuthToken(cloudName, SRCS.INSTAGRAM);
};

const throwOnApiError = (response: Object) => {
	if (response.error) {
		throw new Error(`${response.error.code}: ${response.errormessage}`);
	}
};

const getAccountInfo = async (params: authInfoParams) => {
	const token = params.token || ""; // for flow
	const response = await api.jsonp(`https://graph.instagram.com/me?fields=id,username&access_token=${token}`);

	throwOnApiError(response);

	return {
		user: response.username,
		name: response.username,
	};
};

const getPhotoData = (item, data) => {
	let photoData = null;

	if (item.media_type === instConsts.IMAGE_TYPES.IMAGE) {
		try {
			const imgUrl = item.media_url;

			photoData = {
				id: imgUrl,
				link: imgUrl,
				thumbnail: item.thumbnail_url ? item.thumbnail_url : imgUrl,
				title: "",
				subtitle: data.caption || "",
			};
		} catch (err) {
			logger.error("[instagramService]: failed to parse item data: ", item, data);
		}
	}

	return photoData;
};

const getDataFromItem = (item) => (item.media_type === instConsts.IMAGE_TYPES.CAROUSEL
	? item.children.data.map((image) => getPhotoData(image, item))
	: item.media_type === instConsts.IMAGE_TYPES.IMAGE
		? getPhotoData(item, item)
		: null);

const parseFetchedImages = (data) => data.reduce((res, item) => res.concat(getDataFromItem(item)), [])
	.filter(Boolean); // filter out empty entries

const getMedia = async (params: getParams): Object => {
	const shouldRetrieve = (!params.extend || params.url) && params.cloudName;

	let items = [];
	let nextUrl = null;

	if (shouldRetrieve) {
		const token = getAuthInfo(params.cloudName).token || ""; // for flow... :(
		const url = params.url || `https://graph.instagram.com/me/media?fields=${MEDIA_FIELDS}&limit=${fetchCount}&access_token=${token}`;

		logger.log(`[instagramService]: about to fetch recent media from url: ${url}`);

		const response = await api.jsonp(url);

		throwOnApiError(response);

		items = parseFetchedImages(response.data);
		nextUrl = response.paging && response.paging.next;
	}

	return {
		items: params.extend && params.items ? params.items.concat(items) : items,
		nextUrl,
	};
};

export default () => {
	logger.log("[instagramService]: creating instagram api");

	const authUrl = getAuthenticationUrl();

	const openAuthWindow = (config: Config) => {
		const redirectUrl = servicesCommon.getAuthRedirectUrl(config.newTlsDomain);
		const server = config.server || authUrl;

		let loginUrl = `${server}${endsWith(server, "/") ? "" : "/"}`;
		loginUrl = `${loginUrl}${config.cloudName}/login?redirect_url=${redirectUrl}`;

		logger.log(`[instagramService]: created Instagram login link: ${loginUrl}`);

		authWin = openWindow(loginUrl, "InstagramAuthWin");
	};

	const getTtlHandler = () => tokenTtlHandler;

	return {
		getAuthInfo,
		openAuthWindow,
		setAuthListener,
		logout,
		getAccountInfo,
		getMedia,
		getTtlHandler,
	};
};
